import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Save, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import ComboBox from "@/components/ComboBox";

/**
 * CategoryCreationModal — guided category creation for any Master-Template
 * collection (Wine, Whisky, Spirits). Exported under the historical name
 * `WineCategoryGuidedModal` so existing importers keep working; the file
 * is kept at the same path.
 *
 * Behaviour rebuild (Feb 2026):
 *   - Removed the local DEFAULT_WINE_FIELDS array + per-field POST loop.
 *     The backend `_seed_master_template_for_category` runs automatically
 *     inside POST /api/categories, seeding the correct master template
 *     (Wine / Whisky / Spirits) based on the collection's `type_id`. The
 *     modal now only creates the category and lets the backend own the
 *     schema — this eliminates the duplicate-field bug where the modal
 *     and the backend both inserted the same fields.
 *   - Modal is now collection-type-aware: Wine keeps the "Country → Region
 *     → defaults" flow, Whisky uses "Country → Distillery", Spirits uses
 *     "Country → Brand". Cancel / X / backdrop / Escape all close cleanly.
 *   - Duplicate-name guard: on Save, refuses to create a category whose
 *     trimmed name already exists (case-insensitive) inside the target
 *     collection.
 *
 * Props (unchanged public API):
 *   open           boolean — modal visibility
 *   onClose()      called when the user dismisses (Cancel/X/Escape/backdrop)
 *   onSaved(cat)   called after the category is successfully created
 *   collectionId   string  — target collection the new category attaches to
 *   collectionName string  — used for header + type detection fallback
 *   collectionType string? — optional explicit type key ("wine"|"whisky"|"spirits")
 */
export default function WineCategoryGuidedModal({
  open,
  onClose,
  onSaved,
  collectionId,
  collectionName,
  collectionType,
}) {
  const typeKey = useMemo(() => resolveTypeKey(collectionType, collectionName), [collectionType, collectionName]);
  const spec = TYPE_SPECS[typeKey] || TYPE_SPECS.wine;

  const [categoryName, setCategoryName] = useState("");
  const [country, setCountry] = useState("");
  const [second, setSecond] = useState(""); // Region / Distillery / Brand depending on type
  const [countryOptions, setCountryOptions] = useState([]);
  const [secondOptions, setSecondOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  // Load autocomplete presets. Second-field name varies by type; we still
  // just ask the backend for the two field names we need.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const secondFieldPresetKey = spec.secondPresetKey || spec.secondFieldName;
    (async () => {
      try {
        const names = ["Country", secondFieldPresetKey].join(",");
        const { data } = await apiClient.get(`/presets?field_names=${encodeURIComponent(names)}`);
        if (cancelled) return;
        setCountryOptions(data?.Country || []);
        setSecondOptions(data?.[secondFieldPresetKey] || []);
      } catch {
        if (!cancelled) {
          setCountryOptions([]);
          setSecondOptions([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, spec.secondFieldName, spec.secondPresetKey]);

  // Reset form + refetch each time modal opens.
  useEffect(() => {
    if (open) {
      setCategoryName("");
      setCountry("");
      setSecond("");
    }
  }, [open]);

  // Esc-to-close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const save = async () => {
    const name = categoryName.trim();
    if (!name) { toast.error("Category name is required."); return; }
    if (!collectionId) { toast.error("Missing collection."); return; }

    const trimmedCountry = country.trim();
    const trimmedSecond = second.trim();

    // Duplicate-name prevention within the same collection (case-insensitive).
    try {
      const { data: existing } = await apiClient.get("/categories");
      const clash = (existing || []).find(
        (c) => c.collection_id === collectionId && (c.category_name || "").trim().toLowerCase() === name.toLowerCase()
      );
      if (clash) {
        toast.error(`A category named "${name}" already exists in this collection.`);
        return;
      }
    } catch {
      // Silent — dedupe is a nice-to-have; backend is the source of truth.
    }

    // Build description from whatever context the user provided.
    // Country/Region are now independent optional fields — the description
    // only mentions the ones that were filled in.
    let description = "";
    if (trimmedCountry && trimmedSecond) {
      description = `${spec.descriptionPrefix} ${trimmedCountry}, ${trimmedSecond}`;
    } else if (trimmedCountry) {
      description = `${spec.descriptionPrefix} ${trimmedCountry}`;
    } else if (trimmedSecond) {
      description = `${spec.descriptionPrefix} ${trimmedSecond}`;
    }

    setSaving(true);
    try {
      // Backend auto-seeds the correct Wine / Whisky / Spirits master
      // template based on this collection's type_id. No client-side field
      // creation loop — that was the source of the duplicate-field bug.
      const { data: cat } = await apiClient.post("/categories", {
        category_name: name,
        category_description: description,
        collection_id: collectionId,
      });
      toast.success(`"${name}" created with ${spec.label} master fields.`);
      onSaved?.(cat);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create category.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const modal = (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm vp-fade-in"
      data-testid="wine-cat-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="wine-cat-modal-title"
        className="w-full max-w-md bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-lg shadow-xl flex flex-col max-h-[90vh]"
        data-collection-type={typeKey}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
              <h2
                id="wine-cat-modal-title"
                data-testid="wine-cat-modal-title"
                className="text-base font-semibold tracking-tight"
              >
                {spec.title}
              </h2>
            </div>
            <p className="text-xs vp-muted">
              {spec.subtitle}
              {collectionName ? ` · ${collectionName}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="wine-cat-modal-close"
            aria-label="Close"
            className="vp-btn vp-btn-ghost vp-btn-sm !w-8 !h-8 !p-0 border border-[hsl(var(--border))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs font-medium mb-1.5 vp-muted">
              Category Name <span className="text-[hsl(var(--loss))]">*</span>
            </label>
            <input
              data-testid="wine-cat-modal-name"
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="e.g. French Bordeaux, Islay Single Malts, Tequila Añejo"
              className="vp-input"
              autoFocus
            />
            <p className="text-[10px] vp-muted mt-1.5">
              Type the name yourself — Country and Region below will not overwrite this.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 vp-muted">
              {spec.firstLabel || "Country"} <span className="vp-muted">(optional)</span>
            </label>
            <ComboBox
              testid="wine-cat-modal-country"
              value={country}
              onChange={setCountry}
              options={countryOptions}
              placeholder={spec.countryPlaceholder}
              maxResults={10}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 vp-muted">
              {spec.secondLabel} <span className="vp-muted">(optional)</span>
            </label>
            <ComboBox
              testid="wine-cat-modal-region"
              value={second}
              onChange={setSecond}
              options={secondOptions}
              placeholder={spec.secondPlaceholder}
              maxResults={10}
            />
          </div>

          {/* Master-template preview — informational. The backend seeds
              these fields automatically on category-create. */}
          <div data-testid="wine-cat-modal-fields">
            <span className="text-[10px] uppercase tracking-wider vp-muted block mb-1.5">
              {spec.label} master fields ({spec.previewFields.length})
            </span>
            <div className="flex flex-wrap gap-1.5">
              {spec.previewFields.map((f) => (
                <span
                  key={f}
                  className="text-[10px] px-1.5 py-0.5 rounded border border-[hsl(var(--border))] vp-muted"
                >
                  {f}
                </span>
              ))}
            </div>
            <p className="text-[10px] vp-muted mt-2 italic">
              Master fields are seeded automatically by the backend and can’t be renamed or deleted.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="wine-cat-modal-cancel"
            className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !categoryName.trim()}
            data-testid="wine-cat-modal-save"
            className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Creating…" : "Save Category"}
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}

// ---------------------------------------------------------------------------
// Type detection + per-type specs
// ---------------------------------------------------------------------------
function resolveTypeKey(explicit, name) {
  const e = (explicit || "").trim().toLowerCase();
  if (["wine", "whisky", "spirits", "general", "custom"].includes(e)) return e;
  const n = (name || "").toLowerCase();
  if (/whisk(e)?y/.test(n)) return "whisky";
  if (/(rum|tequila|cognac|gin|vodka|mezcal|armagnac|brandy|liqueur|spirit)/.test(n)) return "spirits";
  if (/wine/.test(n)) return "wine";
  if (/(general|collectable|card|coin|comic|memorabilia|art)/.test(n)) return "general";
  if (/custom/.test(n)) return "custom";
  return "wine"; // safe default — matches historical behaviour
}

// Preview lists mirror the backend TYPE_MASTER_TEMPLATES exactly. They are
// informational-only; the source of truth remains the backend seed. If the
// backend template changes, update these strings to keep the preview in sync.
const TYPE_SPECS = {
  wine: {
    label: "Wine",
    title: "Create a Wine Category",
    subtitle: "Start with Country → Region → defaults",
    countryPlaceholder: "e.g. France, Australia, New Zealand",
    secondLabel: "Region",
    secondPlaceholder: "e.g. Bordeaux, Hawke's Bay, Margaret River",
    secondFieldName: "Region",
    secondPresetKey: "Region",
    descriptionPrefix: "Wine from",
    previewFields: [
      "Producer", "Item Name", "Vintage", "Country", "Region", "Sub-Region",
      "Appellation", "Type", "Grape(s)", "Bottle Size", "ABV", "Classification",
      "Drink From", "Drink To", "Tasting Notes", "Personal Notes",
      "Image", "Storage Location", "Purchase Date", "Purchase Source",
    ],
  },
  whisky: {
    label: "Whisky",
    title: "Create a Whisky Category",
    subtitle: "Start with Country → Distillery → defaults",
    countryPlaceholder: "e.g. Scotland, Japan, Ireland",
    secondLabel: "Distillery",
    secondPlaceholder: "e.g. Bowmore, Yamazaki, Redbreast",
    secondFieldName: "Distillery",
    secondPresetKey: "Distillery",
    descriptionPrefix: "Whisky from",
    previewFields: [
      // CDI industry-standard order (LOCKED — legacy keys removed)
      "ABV", "Age", "Distillery", "Region", "Cask Type", "Bottling Year",
      "Release Type", "Notes",
      // Utility fields
      "Image", "Storage Location", "Purchase Date", "Purchase Source",
    ],
  },
  spirits: {
    label: "Spirits",
    title: "Create a Spirits Category",
    subtitle: "Start with Country → Producer / Distillery → defaults",
    countryPlaceholder: "e.g. Mexico, Japan, Scotland",
    secondLabel: "Producer / Distillery",
    secondPlaceholder: "e.g. Casamigos, Hendrick's, Bacardi",
    secondFieldName: "Producer / Distillery",
    secondPresetKey: "Producer / Distillery",
    descriptionPrefix: "Spirits from",
    previewFields: [
      // CDI industry-standard order (LOCKED — legacy keys removed)
      "Producer / Distillery", "Item Name", "Type", "Country", "Region",
      "ABV", "Age", "Cask Type", "Bottle Size", "Notes",
      // Utility fields
      "Image", "Storage Location", "Purchase Date", "Purchase Source",
    ],
  },
  general: {
    label: "General Collectables",
    title: "Create a General Collectables Category",
    subtitle: "Start with Category → Sub-Category → defaults",
    firstLabel: "Category",
    countryPlaceholder: "e.g. Cards, Coins, Comics, Art",
    secondLabel: "Sub-Category",
    secondPlaceholder: "e.g. Rookie Cards, Silver Coins, Golden Age",
    secondFieldName: "Category",
    secondPresetKey: "Sub-Category",
    descriptionPrefix: "General Collectables —",
    previewFields: [
      // CDI industry-standard order (locked)
      "Item Name", "Category", "Sub-Category", "Year", "Condition",
      "Grade / Certification", "Notes",
      // Utility fields
      "Image", "Storage Location", "Purchase Date", "Purchase Source",
    ],
  },
  custom: {
    label: "Custom",
    title: "Create a Custom Category",
    subtitle: "Universal identifier → defaults",
    firstLabel: "Primary Identifier",
    countryPlaceholder: "Primary identifier (e.g. Series, Set, Line)",
    secondLabel: "Secondary Identifier",
    secondPlaceholder: "Secondary identifier (optional)",
    secondFieldName: "Primary Identifier",
    secondPresetKey: "Secondary Identifier",
    descriptionPrefix: "Custom category —",
    previewFields: [
      // CDI universal order (locked)
      "Primary Identifier", "Secondary Identifier", "Year / Origin",
      "Condition / Grade", "Notes", "Image", "Storage", "Purchase Info",
    ],
  },
};
